/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Icon,
    Link,
    SPACING_M,
    SPACING_XL,
    STACK_ALIGN,
    STACK_DIRECTION,
    STACK_GAP,
    StackView,
    TEXT_VIEW,
    Text,
} from '@veeam/components';
import styled from 'styled-components';

import type { SelectLinkProps, LinkButtonProps } from '@veeam/components';

import { TextColumn } from '../TextColumn';
import { FullPageButton } from '../../../FullPageButton';
import { SelectLink } from '../../../SelectLink';
import { LinkButton } from '../../../LinkButton';
import { ColumnTexts } from '../../../../enums';

import type { TextColumnProps } from '../TextColumn';
import type { FullPageButtonProps } from '../../../FullPageButton';
import type { UseFullPageProps } from '../../../../hooks/use-full-page';

const getTextView = (disabled?: boolean, error?: boolean): TEXT_VIEW => {
    if (disabled) {
        return TEXT_VIEW.light;
    } else if (error) {
        return TEXT_VIEW.error;
    }

    return TEXT_VIEW.primary;
};

const StyledStackView = styled(StackView)<{ compact: boolean; }>`
    height: ${({ compact }) => compact ? SPACING_M : SPACING_XL};
`;

export interface ActionLinkColumnProps extends Partial<SelectLinkProps>,
    Partial<Omit<TextColumnProps, 'children'>>, Partial<Omit<FullPageButtonProps, 'renderFullPageWindow' | 'children'>> {
    content?: React.ReactNode;
    isLink?: boolean;
    renderPageDialog?: UseFullPageProps['render'];
    href?: string;
}

const isNullOrEmpty = obj => obj == null || (typeof obj === 'string' && obj.trim().length === 0);

export const ActionLinkColumn: React.FC<ActionLinkColumnProps> = (props) => {
    const {
        content,
        isLink = true,
        renderPageDialog,
        disabled,
        error,
        compact,
        contentIconBefore,
        contentIconAfter,
        renderContent,
        onClick,
        renderModal,
        href,
        ...restProps
    } = props;

    if (isLink) {
        if (renderPageDialog) {
            return (
                <FullPageButton
                    {...restProps}
                    disabled={disabled}
                    error={error}
                    iconBefore={contentIconBefore}
                    compact={compact}
                    iconAfter={contentIconAfter}
                    renderContent={renderContent}
                    renderFullPageWindow={renderPageDialog}
                >
                    {isNullOrEmpty(content) ? ColumnTexts.DashSymbol : content}
                </FullPageButton>
            );
        } else if (typeof renderModal === 'function') {
            const columnContent = (isNullOrEmpty(content) && renderContent)
                ? renderContent({ error, disabled })
                : content;

            const shouldRenderTooltip = !!columnContent || columnContent === 0;

            const renderContentFn = shouldRenderTooltip
                ? () => <TextColumn tooltipText={columnContent}>{columnContent}</TextColumn>
                : renderContent;

            return (
                <SelectLink
                    {...restProps as SelectLinkProps}
                    onClick={onClick}
                    disabled={disabled}
                    error={error}
                    compact={compact}
                    contentIconBefore={contentIconBefore}
                    contentIconAfter={contentIconAfter}
                    renderContent={renderContentFn}
                    renderModal={renderModal}
                />
            );
        } else if (href) {
            return (
                <Link
                    href={href}
                >
                    {content}
                </Link>
            );
        } else {
            return (
                <LinkButton
                    {...restProps as LinkButtonProps}
                    onClick={onClick}
                    disabled={disabled}
                    error={error}
                    compact={compact}
                    iconBefore={contentIconBefore}
                    iconAfter={contentIconAfter}
                >
                    {isNullOrEmpty(content) ? ColumnTexts.DashSymbol : content}
                </LinkButton>
            );
        }
    }

    return (
        <StyledStackView
            align={STACK_ALIGN.center}
            direction={STACK_DIRECTION.row}
            gap={STACK_GAP.s}
            compact={compact}
        >
            {contentIconBefore && (
                <Icon src={contentIconBefore} disabled={disabled} />
            )}

            <Text
                {...restProps}
                truncate
                view={getTextView(disabled, error)}
                style={{
                    width: '100%',
                    textAlign: 'inherit',
                }}
            >
                {renderContent?.({ error, disabled }) || content || ColumnTexts.DashSymbol}
            </Text>

            {contentIconAfter && (
                <Icon src={contentIconAfter} disabled={disabled} />
            )}
        </StyledStackView>
    );
};
